import { createAsyncThunk, createSelector, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'; // Import createSelector
import { RootState } from 'app/store';
import { selectCertificateTypes } from 'features/certificateTypes/certificateTypesSlice';
import { NewCertificateInterface } from 'interfaces/Certificate';
import { RequestStatus } from 'interfaces/RequestStatus';
import { createSellerInterface, fetchCreateSeller } from './fetchSeller';

interface NewSellerState {
  eldat_reference_id: string;
  taxation_kind_id: string;
  agent_id: string;
  certificates: NewCertificateInterface[];
  status: RequestStatus;
  errors: any;
}

const initialState: NewSellerState = {
  eldat_reference_id: '',
  taxation_kind_id: '',
  agent_id: '',
  certificates: [],
  status: RequestStatus.idle,
  errors: null,
};

export const createSeller = createAsyncThunk(
  "seller/fetchCreateSeller",
  async (_, { getState, rejectWithValue }) => {
    const { user, newSeller } = getState() as RootState;
    if (!user || !user.role) {
      throw new Error("User role is undefined.");
    }
    const seller: createSellerInterface = {
      eldat_reference_id: newSeller.eldat_reference_id,
      taxation_kind_id: newSeller.taxation_kind_id,
      agent_id: newSeller.agent_id,

      certificates: newSeller.certificates.filter(certificate => !!!certificate._destroy).map(certificate => {
        // Remove id because it is not needed for creation
        const { id, ...certificateWithoutId } = certificate;
        return certificateWithoutId;
      })
    };
    const response = await fetchCreateSeller({ seller, endpoint: user.role });
    if (response.status === 200) {
      return { headers: response.headers, data: response.data, status: response.status };
    }
    return rejectWithValue(response.data.data.errors);
  }
);


export const newSellerSlice = createSlice({
  name: 'newSeller',
  initialState,
  reducers: {
    setSellerNumber: (state, action: PayloadAction<string>) => {
      state.eldat_reference_id = action.payload;
    },
    setTaxType: (state, action: PayloadAction<string>) => {
      state.taxation_kind_id = action.payload;
    },
    setAgentId: (state, action: PayloadAction<string>) => {
      state.agent_id = action.payload;
    },
    setCertificates: (state, action: PayloadAction<NewCertificateInterface[]>) => {
      state.certificates = action.payload;
    },
    addCertificate: (state, action: PayloadAction<NewCertificateInterface>) => {
      state.certificates.push(action.payload);
    },
    removeLastCertificate: (state) => {
      if (state.certificates.length > 0) {
        state.certificates.pop();
        state.certificates = [...state.certificates];
      }
    },
    updateCertificate: (state, action: PayloadAction<NewCertificateInterface>) => {
      const updatedCertificate = action.payload;
      const index = state.certificates.findIndex((certificate) => certificate.id === updatedCertificate.id);
      if (index >= 0 && index < state.certificates.length) {
        state.certificates[index] = updatedCertificate;
      } else {
        console.error("Invalid index for certificate update:", index);
      }
    },
    removeCertificate: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const index = state.certificates.findIndex((certificate) => certificate.id === id);
      if (index >= 0 && index < state.certificates.length) {
        state.certificates[index]._destroy = true;
      } else {
        console.error("Invalid index for certificate removal:", index);
      }
    },
    resetNewSeller: (state) => {
      state.eldat_reference_id = '';
      state.taxation_kind_id = '';
      state.agent_id = '';
      state.certificates = [];
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(createSeller.fulfilled, (state, action) => {
        state.status = RequestStatus.idle;
        // console.log(action.payload.data);
        state.errors = action.payload.data.data;
      })
      .addMatcher(isAnyOf(createSeller.pending), (state) => {
        state.status = RequestStatus.loading;
        state.errors = null; // Reset errors on failure
      })
      .addMatcher(isAnyOf(createSeller.rejected), (state) => {
        state.status = RequestStatus.failed;
        state.errors = "An error occurred while creating the seller."; // Set a generic error message
      });
  }
});

export const { setSellerNumber, setTaxType, setAgentId, setCertificates, addCertificate, removeLastCertificate, removeCertificate, resetNewSeller, updateCertificate } = newSellerSlice.actions;
export const selectSellerNumber = (state: RootState) => state.newSeller.eldat_reference_id;
export const selectTaxType = (state: RootState) => state.newSeller.taxation_kind_id;
export const selectAgentId = (state: RootState) => state.newSeller.agent_id;

// Input selector for the raw certificates array
const selectRawCertificates = (state: RootState) => state.newSeller.certificates;

// Memoized selector for filtered certificates
export const selectCertificates = createSelector(
  [selectRawCertificates], // Array of input selectors
  (certificates) => certificates.filter((certificate) => certificate._destroy !== true) // Result function
);

// Selector to get the id parameter itself
const selectCertificateId = (_state: RootState, id: string) => id;

// Memoized selector to find a specific certificate by id
export const selectCertificateById = createSelector(
  [selectRawCertificates, selectCertificateId], // Input selectors: all certificates and the id
  (certificates, id) => {
    return certificates.find((certificate) => certificate.id === id);
  }
);

export const selectCertificatesWithShortname = createSelector(
  [selectCertificates, selectCertificateTypes],
  (certificates, certificateTypes) => {
    return certificates.map((certificate) => {
      const shortname = certificateTypes.find((type) => type.id === certificate.certificate_type_id)?.short_name;
      return {
        ...certificate,
        shortname: shortname ? shortname : "Zertifikat",
      };
    });
  }
);

export default newSellerSlice.reducer;